/**
 * Master Layout — ISOLATED, FLAG-GATED mock backend.
 *
 * Enabled ONLY when VUE_APP_MASTER_LAYOUT_MOCK === "true". The store never
 * falls back to this mock on an Axios error.
 *
 * Payloads use the aligned Part 1 vocabulary. A placement OWNS:
 *   entity_id, canonical_question_id, sort_order, inclusion, widget_override,
 *   conditional
 * and carries a nested read-only `canonical` object (machine_key, title,
 * display_label, answer_options, widget_type, help_text) — emulating the BE
 * join so the UI can display canonical content. Placement creation receives
 * only `canonical_question_id`; the mock resolves display content from a
 * dev-only canonical registry primed by the store (primeMockCanonicalQuestions).
 *
 * Master Layout has no separate publish lifecycle: edits are drafts that flow
 * into the existing Draft -> Validate -> Publish workflow.
 *
 * Dispatcher: `mockMasterLayoutRequest(method, path, body)` -> `{ data }`.
 */

/* eslint-disable no-use-before-define */

export function isMasterLayoutMockEnabled() {
  return process.env.VUE_APP_MASTER_LAYOUT_MOCK === "true";
}

let idCounter = 1000;
function nextId(prefix) {
  idCounter += 1;
  return `${prefix}_${idCounter}`;
}

function clone(value) {
  return structuredClone(value);
}

/* ------------------------------------------------------------------ */
/* Dev-only canonical registry (emulates the BE canonical join)        */
/* ------------------------------------------------------------------ */

let canonicalRegistry = {};

/**
 * Prime the mock with canonical-question display data so newly created
 * placements can be shown enriched — mirrors what the real BE returns nested.
 * Accepts questions in the aligned vocabulary (see mapCanonicalQuestion).
 */
export function primeMockCanonicalQuestions(questions = []) {
  for (const q of questions || []) {
    if (q?.entity_id == null) continue;
    canonicalRegistry[q.entity_id] = {
      entity_id: q.entity_id,
      machine_key: q.machine_key ?? "",
      title: q.title ?? "",
      display_label: q.display_label ?? "",
      answer_options: q.answer_options ?? [],
      widget_type: q.widget_type ?? null,
      help_text: q.help_text ?? "",
    };
  }
}

function canonicalSnapshot(canonicalQuestionId) {
  const found = canonicalRegistry[canonicalQuestionId];
  if (found) return { ...found };
  return {
    entity_id: canonicalQuestionId ?? null,
    machine_key: "",
    title: "",
    display_label: "",
    answer_options: [],
    widget_type: null,
    help_text: "",
  };
}

/* ------------------------------------------------------------------ */
/* Seed layout                                                         */
/* ------------------------------------------------------------------ */

function seedPlacement(o) {
  return {
    entity_id: o.entity_id,
    canonical_question_id: o.canonical_question_id,
    sort_order: o.sort_order ?? 0,
    inclusion: o.inclusion ?? "always_on",
    widget_override: o.widget_override ?? null,
    conditional: o.conditional ?? null,
    // read-only canonical display (emulates BE join)
    canonical: {
      entity_id: o.canonical_question_id,
      machine_key: o.machine_key ?? "",
      title: o.title ?? "",
      display_label: o.display_label ?? o.title ?? "",
      answer_options: o.answer_options ?? [],
      widget_type: o.widget_type ?? null,
      help_text: o.help_text ?? "",
    },
  };
}

function buildSeedLayout() {
  return {
    entity_id: "ml_master",
    title: "Company Workbook — Master Layout",
    version: 3,
    status: "draft",
    stages: [
      {
        entity_id: "stage_company",
        machine_key: "company_profile",
        title: "Company Profile",
        sort_order: 0,
        screens: [
          {
            entity_id: "screen_company_details",
            machine_key: "company_details",
            title: "Company Details",
            stage_id: "stage_company",
            sort_order: 0,
            placements: [
              seedPlacement({
                entity_id: "plc_legal_name",
                canonical_question_id: "1",
                machine_key: "company.legal_name",
                title: "Legal company name",
                display_label: "Legal company name",
                widget_type: "text",
                sort_order: 0,
                inclusion: "always_on",
              }),
              seedPlacement({
                entity_id: "plc_hq_address",
                canonical_question_id: "2",
                machine_key: "company.hq_address",
                title: "Headquarters address",
                display_label: "Headquarters address",
                widget_type: "address",
                sort_order: 1,
                inclusion: "always_on",
                widget_override: "address",
              }),
            ],
          },
          {
            entity_id: "screen_industry",
            machine_key: "industry",
            title: "Industry & Operations",
            stage_id: "stage_company",
            sort_order: 1,
            placements: [
              seedPlacement({
                entity_id: "plc_industry",
                canonical_question_id: "3",
                machine_key: "company.industry",
                title: "Primary industry",
                display_label: "Primary industry",
                widget_type: "industry_select",
                answer_options: ["Technology", "Manufacturing", "Healthcare", "Finance"],
                sort_order: 0,
                inclusion: "always_on",
                widget_override: "industry_select",
              }),
            ],
          },
        ],
      },
      {
        entity_id: "stage_security",
        machine_key: "security_posture",
        title: "Security Posture",
        sort_order: 1,
        screens: [
          {
            entity_id: "screen_controls",
            machine_key: "controls",
            title: "Security Controls",
            stage_id: "stage_security",
            sort_order: 0,
            placements: [
              seedPlacement({
                entity_id: "plc_mfa",
                canonical_question_id: "4",
                machine_key: "security.mfa_enabled",
                title: "Is MFA enabled across the org?",
                display_label: "MFA enabled?",
                widget_type: "yes_no",
                sort_order: 0,
                inclusion: "carrier_derived",
              }),
              seedPlacement({
                entity_id: "plc_mfa_coverage",
                canonical_question_id: "5",
                machine_key: "security.mfa_coverage",
                title: "What percentage of accounts have MFA?",
                display_label: "MFA coverage",
                widget_type: "percentage",
                sort_order: 1,
                inclusion: "always_on",
                widget_override: "percentage",
                // parentCanonicalId references the MFA question's ID ("4")
                conditional: {
                  op: "AND",
                  clauses: [{ parentCanonicalId: "4", operator: "==", value: "Yes" }],
                },
              }),
            ],
          },
        ],
      },
    ],
  };
}

let layout = buildSeedLayout();

export function __resetMasterLayoutMock() {
  layout = buildSeedLayout();
  canonicalRegistry = {};
  idCounter = 1000;
}

/* ------------------------------------------------------------------ */
/* Lookup helpers                                                      */
/* ------------------------------------------------------------------ */

function findStage(id) {
  return layout.stages.find((s) => s.entity_id === id) || null;
}
function allScreens() {
  return layout.stages.flatMap((s) => s.screens);
}
function findScreen(id) {
  return allScreens().find((sc) => sc.entity_id === id) || null;
}
function findScreenParent(id) {
  return layout.stages.find((s) => s.screens.some((sc) => sc.entity_id === id)) || null;
}
function allPlacements() {
  return allScreens().flatMap((sc) => sc.placements);
}
function findPlacementScreen(id) {
  return allScreens().find((sc) => sc.placements.some((p) => p.entity_id === id)) || null;
}

function reindex(list) {
  for (const [i, item] of list.entries()) {
    item.sort_order = i;
  }
}

function reorderById(list, orderedIds) {
  const byId = new Map(list.map((item) => [item.entity_id, item]));
  const reordered = orderedIds.map((id) => byId.get(id)).filter(Boolean);
  for (const item of list) {
    if (!orderedIds.includes(item.entity_id)) reordered.push(item);
  }
  reindex(reordered);
  return reordered;
}

/* ------------------------------------------------------------------ */
/* Request dispatcher                                                  */
/* ------------------------------------------------------------------ */

function ok(payload = {}) {
  return { data: { success: true, ...payload } };
}
function fail(message) {
  return { data: { success: false, message } };
}

const ROUTES = [
  {
    method: "GET",
    re: /^\/admin\/master-layout$/,
    handler: () => ok({ layout: clone(layout) }),
  },

  /* ---- stages ---- */
  {
    method: "POST",
    re: /^\/admin\/master-layout\/stages$/,
    handler: (_m, body) => {
      const stage = {
        entity_id: nextId("stage"),
        machine_key: (body.title || "stage").toLowerCase().replaceAll(/\s+/g, "_"),
        title: body.title || "Untitled stage",
        sort_order: layout.stages.length,
        screens: [],
      };
      layout.stages.push(stage);
      return ok({ stage: clone(stage) });
    },
  },
  {
    method: "PUT",
    re: /^\/admin\/master-layout\/stages\/reorder$/,
    handler: (_m, body) => {
      layout.stages = reorderById(layout.stages, body.order || []);
      return ok({ stages: clone(layout.stages) });
    },
  },
  {
    method: "PUT",
    re: /^\/admin\/master-layout\/stages\/([^/]+)$/,
    handler: (m, body) => {
      const stage = findStage(m[1]);
      if (!stage) return fail("Stage not found");
      if (body.title !== undefined) stage.title = body.title;
      return ok({ stage: clone(stage) });
    },
  },
  {
    method: "DELETE",
    re: /^\/admin\/master-layout\/stages\/([^/]+)$/,
    handler: (m) => {
      const before = layout.stages.length;
      layout.stages = layout.stages.filter((s) => s.entity_id !== m[1]);
      if (layout.stages.length === before) return fail("Stage not found");
      reindex(layout.stages);
      return ok();
    },
  },

  /* ---- screens ---- */
  {
    method: "POST",
    re: /^\/admin\/master-layout\/stages\/([^/]+)\/screens$/,
    handler: (m, body) => {
      const stage = findStage(m[1]);
      if (!stage) return fail("Stage not found");
      const screen = {
        entity_id: nextId("screen"),
        machine_key: (body.title || "screen").toLowerCase().replaceAll(/\s+/g, "_"),
        title: body.title || "Untitled screen",
        stage_id: stage.entity_id,
        sort_order: stage.screens.length,
        placements: [],
      };
      stage.screens.push(screen);
      return ok({ screen: clone(screen) });
    },
  },
  {
    method: "PUT",
    re: /^\/admin\/master-layout\/stages\/([^/]+)\/screens\/reorder$/,
    handler: (m, body) => {
      const stage = findStage(m[1]);
      if (!stage) return fail("Stage not found");
      stage.screens = reorderById(stage.screens, body.order || []);
      return ok({ screens: clone(stage.screens) });
    },
  },
  {
    method: "PUT",
    re: /^\/admin\/master-layout\/screens\/([^/]+)\/move$/,
    handler: (m, body) => {
      const screen = findScreen(m[1]);
      const target = findStage(body.stage_id);
      if (!screen || !target) return fail("Screen or target stage not found");
      const source = findScreenParent(screen.entity_id);
      source.screens = source.screens.filter((sc) => sc.entity_id !== screen.entity_id);
      reindex(source.screens);
      screen.stage_id = target.entity_id;
      screen.sort_order = target.screens.length;
      target.screens.push(screen);
      return ok({ screen: clone(screen) });
    },
  },
  {
    method: "PUT",
    re: /^\/admin\/master-layout\/screens\/([^/]+)$/,
    handler: (m, body) => {
      const screen = findScreen(m[1]);
      if (!screen) return fail("Screen not found");
      if (body.title !== undefined) screen.title = body.title;
      return ok({ screen: clone(screen) });
    },
  },
  {
    method: "DELETE",
    re: /^\/admin\/master-layout\/screens\/([^/]+)$/,
    handler: (m) => {
      const stage = findScreenParent(m[1]);
      if (!stage) return fail("Screen not found");
      stage.screens = stage.screens.filter((sc) => sc.entity_id !== m[1]);
      reindex(stage.screens);
      return ok();
    },
  },

  /* ---- placements ---- */
  {
    method: "POST",
    re: /^\/admin\/master-layout\/screens\/([^/]+)\/placements$/,
    handler: (m, body) => {
      const screen = findScreen(m[1]);
      if (!screen) return fail("Screen not found");
      // Placement is created from ONLY the placement-owned field
      // (canonical_question_id); canonical display is joined from the registry.
      const snap = canonicalSnapshot(body.canonical_question_id);
      const placement = {
        entity_id: nextId("plc"),
        canonical_question_id: body.canonical_question_id ?? null,
        sort_order: screen.placements.length,
        inclusion: "always_on",
        widget_override: null,
        conditional: null,
        canonical: snap,
      };
      screen.placements.push(placement);
      return ok({ placement: clone(placement) });
    },
  },
  {
    method: "PUT",
    re: /^\/admin\/master-layout\/screens\/([^/]+)\/placements\/reorder$/,
    handler: (m, body) => {
      const screen = findScreen(m[1]);
      if (!screen) return fail("Screen not found");
      screen.placements = reorderById(screen.placements, body.order || []);
      return ok({ placements: clone(screen.placements) });
    },
  },
  {
    method: "PUT",
    re: /^\/admin\/master-layout\/placements\/([^/]+)\/move$/,
    handler: (m, body) => {
      const placement = allPlacements().find((p) => p.entity_id === m[1]);
      const target = findScreen(body.screen_id);
      if (!placement || !target) return fail("Placement or target screen not found");
      const source = findPlacementScreen(placement.entity_id);
      source.placements = source.placements.filter((p) => p.entity_id !== placement.entity_id);
      reindex(source.placements);
      placement.sort_order = target.placements.length;
      target.placements.push(placement);
      return ok({ placement: clone(placement) });
    },
  },
  {
    method: "PUT",
    re: /^\/admin\/master-layout\/placements\/([^/]+)$/,
    handler: (m, body) => {
      const placement = allPlacements().find((p) => p.entity_id === m[1]);
      if (!placement) return fail("Placement not found");
      // Only placement-OWNED fields are editable (display_label is NOT).
      const EDITABLE = ["inclusion", "widget_override", "conditional"];
      for (const field of EDITABLE) {
        if (Object.hasOwn(body, field)) placement[field] = body[field];
      }
      return ok({ placement: clone(placement) });
    },
  },
  {
    method: "DELETE",
    re: /^\/admin\/master-layout\/placements\/([^/]+)$/,
    handler: (m) => {
      const screen = findPlacementScreen(m[1]);
      if (!screen) return fail("Placement not found");
      screen.placements = screen.placements.filter((p) => p.entity_id !== m[1]);
      reindex(screen.placements);
      return ok();
    },
  },
];

export function mockMasterLayoutRequest(method, path, body = {}) {
  const route = ROUTES.find((r) => r.method === method && r.re.test(path));
  if (!route) {
    return Promise.resolve(fail(`No mock route for ${method} ${path}`));
  }
  const match = path.match(route.re);
  return Promise.resolve(route.handler(match, body || {}));
}
